// client/src/components/ClientPubComplet.jsx

import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ClientNavbar from './ClientNavbar';
import '../CSS/Dashboard.css';
import { FaArrowLeft } from 'react-icons/fa';

// --- Utils ---
const kindFromContentType = (ct = '') => {
  if (!ct) return null;
  if (ct.startsWith('image/')) return 'image';
  if (ct === 'application/pdf') return 'pdf';
  if (ct.startsWith('video/')) return 'video';
  return 'other';
};
const kindFromUrlExt = (url = '') => {
  const lower = (url || '').toLowerCase();
  if (lower.endsWith('.png') || lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.webp')) return 'image';
  if (lower.endsWith('.pdf')) return 'pdf';
  if (lower.endsWith('.mp4')) return 'video';
  return 'other';
};

// --- Index/cache (aligné avec clientpromo / clientinvoices) ---
const PROMO_CACHE_KEY = 'clientpromo_lastMerchant';
const PROMO_CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6h : identique à ClientPromo
const nowMs = () => Date.now();

const serializePromo = (pub = {}) => ({
  url: pub.url || '',
  name: pub.name || '',
  contentType: pub.contentType || '',
  start: pub.start instanceof Date ? pub.start.toISOString() : pub.start || null,
  end: pub.end instanceof Date ? pub.end.toISOString() : pub.end || null,
  timeCreated: pub.timeCreated instanceof Date ? pub.timeCreated.toISOString() : pub.timeCreated || null,
});

const serializeMerchant = (m = {}) => ({
  uid: m.uid || '',
  pseudo: m.pseudo || '',
  profilePhotoUrl: m.profilePhotoUrl || '',
  latestMs: m.latestMs || m.promos?.[0]?.timeCreated?.getTime?.() || null,
  promos: Array.isArray(m.promos) ? m.promos.map(serializePromo) : [],
});

// merchantSignature retiré (index distant supprimé)

const deserializeMerchant = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  const promos = Array.isArray(payload.promos)
    ? payload.promos.map((p) => ({
        ...p,
        start: p?.start ? new Date(p.start) : null,
        end: p?.end ? new Date(p.end) : null,
        timeCreated: p?.timeCreated ? new Date(p.timeCreated) : null,
      }))
    : [];
  return {
    uid: payload.uid || '',
    pseudo: payload.pseudo || '',
    profilePhotoUrl: payload.profilePhotoUrl || '',
    latestMs: payload.latestMs || null,
    promos,
  };
};

const loadCachedMerchant = () => {
  try {
    const raw = localStorage.getItem(PROMO_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const ts = parsed?.ts || 0;
    if (!ts || nowMs() - ts > PROMO_CACHE_TTL_MS) return null;
    return deserializeMerchant(parsed.merchant);
  } catch {
    return null;
  }
};

const saveCachedMerchant = (merchant) => {
  try {
    localStorage.setItem(
      PROMO_CACHE_KEY,
      JSON.stringify({ ts: nowMs(), merchant: serializeMerchant(merchant) })
    );
  } catch {
    // storage indisponible => on continue silencieusement
  }
};

// Miniature unifiée (non cliquable → clic géré par le parent)
const Thumb = React.memo(function Thumb({ pub }) {
  const kind = useMemo(() => {
    const byMeta = kindFromContentType(pub.contentType);
    return byMeta || kindFromUrlExt(pub.url);
  }, [pub.contentType, pub.url]);

  const mediaStyle = {
    width: '100%',
    height: '100%',
    borderRadius: 0,
    objectFit: 'cover',
    display: 'block',
    pointerEvents: 'none', // évite que l'iframe vidéo/pdf bloque le clic
  };

  if (kind === 'image') {
    return <img loading="lazy" src={pub.url} alt={pub.name || 'Promotion'} style={mediaStyle} />;
  }
  if (kind === 'video') {
    return <video src={pub.url} muted playsInline preload="metadata" style={mediaStyle} />;
  }
  if (kind === 'pdf') {
    return (
      <iframe
        title={pub.name || 'PDF'}
        src={`${pub.url}#zoom=page-fit&scrollbar=0&toolbar=0&navpanes=0`}
        loading="lazy"
        style={{ ...mediaStyle, border: 'none', background: '#fff' }}
      />
    );
  }
  return (
    <div
      style={{
        ...mediaStyle,
        background: 'var(--eb-surface-2)',
        border: '1px solid #eee',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        color: 'var(--eb-muted)',
      }}
    >
      Fichier
    </div>
  );
});

// Item de grille mémoïsé (évite rerender inutiles)
const PromoItem = React.memo(function PromoItem({ pub, onOpen, logoUrl, pseudo }) {
  const onClick = useCallback(() => {
    onOpen(pub.url, logoUrl, pseudo);
  }, [onOpen, pub.url, logoUrl, pseudo]);

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onOpen(pub.url, logoUrl, pseudo);
      }
    },
    [onOpen, pub.url, logoUrl, pseudo]
  );

  return (
    <div
      className="pub-item"
      onClick={onClick}
      onKeyDown={onKeyDown}
      tabIndex={0}
      role="button"
      title={pub.name || 'Promotion'}
    >
      <Thumb pub={pub} />
    </div>
  );
});

const ClientPubComplet = () => {
  const location = useLocation();
  const [merchant, setMerchant] = useState(() => location.state || null);
  const navigate = useNavigate();

  // Hydrate depuis la navigation (source la plus fraîche)
  useEffect(() => {
    if (location.state) {
      setMerchant(location.state);
    }
  }, [location.state]);

// Fallback : cache local ; pas d’index Firestore (on veut éviter les écritures)
// Index distant retiré : no-op

// Fallback : cache local uniquement (évite re-listAll côté client)
useEffect(() => {
  if (merchant) return;

  const cached = loadCachedMerchant();
  if (cached) {
      setMerchant(cached);
      return;
    }

    // rien à faire si pas de merchant en navigation ou en cache
  }, [merchant]);

  // Persiste dans le cache (pas d’index distant)
  useEffect(() => {
    if (!merchant) return;
    saveCachedMerchant(merchant);
  }, [merchant]);

  // Hooks au top-level (jamais conditionnels)
  const encodedLogo = useMemo(
    () => encodeURIComponent(merchant?.profilePhotoUrl || ''),
    [merchant?.profilePhotoUrl]
  );
  const encodedPseudo = useMemo(
    () => encodeURIComponent(merchant?.pseudo || ''),
    [merchant?.pseudo]
  );

  const handleBackClick = useCallback(() => {
    navigate('/clientpromo');
  }, [navigate]);

  // Redirige vers clientpubviewer (handler stable)
  const handleViewPhoto = useCallback(
    (pubUrl /*, logoUrl, pseudo */) => {
      const encodedUrl = encodeURIComponent(pubUrl || '');
      navigate(`/clientpubviewer?url=${encodedUrl}&logo=${encodedLogo}&pseudo=${encodedPseudo}`);
    },
    [navigate, encodedLogo, encodedPseudo]
  );

  // Promos mémoïsées (retourne [] si merchant absent)
  const promos = useMemo(() => merchant?.promos || [], [merchant?.promos]);

  return (
    <div className="dashboard-container cpc-page">
      <ClientNavbar />
      <style>{CPC_STYLES}</style>

      <div className="cpc-inner">
        <header className="eb-page-header">
          <div>
            <h1 className="eb-page-title cpc-title">Promotions</h1>
          </div>
          <button type="button" onClick={handleBackClick} className="eb-btn eb-btn--secondary">
            <FaArrowLeft aria-hidden="true" /> Retour
          </button>
        </header>

        {!merchant ? (
          <div className="cpc-empty">Aucune donnée de commerçant trouvée.</div>
        ) : (
          <section className="cpc-card">
            {/* Header avec logo et pseudo */}
            <div className="merchant-header cpc-head">
              {merchant.profilePhotoUrl && (
                <img
                  loading="lazy"
                  src={merchant.profilePhotoUrl}
                  alt="Logo"
                  className="cpc-logo"
                />
              )}
              <h2 className="cpc-name" title={merchant.pseudo}>
                {merchant.pseudo}
              </h2>
            </div>

            {/* Toutes les promos */}
            <div className="promos-grid cpc-grid">
              {promos.map((pub, idx) => (
                <PromoItem
                  key={`${pub.name || 'promo'}_${idx}`}
                  pub={pub}
                  onOpen={handleViewPhoto}
                  logoUrl={merchant.profilePhotoUrl}
                  pseudo={merchant.pseudo}
                />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

const CPC_STYLES = `
.dashboard-container.cpc-page { align-items: stretch; }
.cpc-inner { width: 100%; max-width: 760px; margin: 0 auto; }
.dashboard-container .cpc-title { text-align: left; margin: 0; font-size: 26px; }
.cpc-card {
  padding: 20px;
  background: var(--eb-surface);
  border: 1px solid var(--eb-border);
  border-radius: var(--eb-radius-lg);
  box-shadow: var(--eb-shadow-sm);
}
.cpc-head {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--eb-border);
}
.cpc-logo { width: 48px; height: 48px; flex-shrink: 0; border-radius: 50%; object-fit: cover; border: 1px solid var(--eb-border); }
.dashboard-container h2.cpc-name {
  margin: 0;
  min-width: 0;
  text-align: left;
  font-size: 18px;
  font-weight: 650;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cpc-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
.cpc-grid .pub-item {
  cursor: pointer;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  border-radius: var(--eb-radius);
  background: var(--eb-surface-2);
  border: 1px solid var(--eb-border);
  transition: transform .15s ease, box-shadow .15s ease;
}
.cpc-grid .pub-item:hover { transform: scale(1.03); box-shadow: var(--eb-shadow-md); }
.cpc-grid .pub-item:focus-visible { outline: none; box-shadow: var(--eb-ring); }
.cpc-empty {
  padding: 40px 20px;
  text-align: center;
  color: var(--eb-muted);
  background: var(--eb-surface);
  border: 1px solid var(--eb-border);
  border-radius: var(--eb-radius-lg);
  box-shadow: var(--eb-shadow-sm);
}
@media (max-width: 600px) {
  .dashboard-container .cpc-title { font-size: 22px; }
  .cpc-card { padding: 16px; }
  .cpc-grid { gap: 8px; }
}
`;

export default ClientPubComplet;
